// Copyright 2017 Sorama B.V.

import { computedFrom, inject, observable } from "aurelia-framework";
import { WorkflowModalCatalog } from "catalogs/WorkflowModalCatalog";
import { EditableField } from "controls/editable-field/EditableField";
import { FrontEnd, Point3f } from "datatypes/SoramaLocalApi";
import {
    IWorkflowModuleActivate,
    IWorkflowModuleInstance,
} from "libraries/WorkflowFramework/Interfaces/IWorkflowModule";
import { WorkflowPropertyBindingMode } from "libraries/WorkflowFramework/Interfaces/IWorkflowPropertyMetadata";
import { workflowProperty } from "libraries/WorkflowFramework/WorkflowProperties/WorkflowPropertyAttribute";
import {
    ChannelSelectionModalReturnState,
    ChannelSelectionModalState,
} from "modules/modals/ChannelSelection/ChannelSelectionModal";
import { IGenericRepository } from "repositories/IGenericRepository";
import { MeasurementSetupRepository } from "repositories/MeasurementSetupRepository";
import { ChannelSelection } from "services/ChannelSelection/ChannelSelection";
import { IChannelSelectionPresetService } from "services/ChannelSelection/IChannelSelectionPresetService";
import { MeasurementDevice } from "services/Hardware/Datatypes/MeasurementDevice";
import {
    ConnectedMeasurementDeviceSetup,
    MeasurementDeviceSetup,
    MeasurementDeviceSetupInfo,
} from "services/Hardware/Datatypes/MeasurementDeviceSetup";
import { IHardwareService } from "services/Hardware/IHardwareService";
import { IReservableHardwareService } from "services/Hardware/IReservableHardwareService";
import { IMeasurementAcquisitionService } from "services/Measurement/IMeasurementAcquisitionService";
import { NotificationUrgency } from "services/Notification/INotificationService";

@inject(
    MeasurementSetupRepository,
    "IGenericRepository<MeasurementDevice,MeasurementDevice>",
    "IHardwareService<MeasurementDevice>",
    "IReservableHardwareService<FrontEnd,string>",
    IMeasurementAcquisitionService,
    IChannelSelectionPresetService
)
export class MeasurementDeviceModule implements IWorkflowModuleActivate {
    //#region Workflow Propertes
    @workflowProperty({
        bindingMode: WorkflowPropertyBindingMode.twoWay,
        changedHandler: "selectedMeasurementSetupOrInfoIdChanged",
    })
    private selectedMeasurementSetupId: string;

    @workflowProperty({
        bindingMode: WorkflowPropertyBindingMode.twoWay,
        changedHandler: "selectedMeasurementSetupOrInfoIdChanged",
    })
    private selectedMeasurementDeviceSetupInfoId: string;
    //#endregion

    private measurementDeviceSetup: MeasurementDeviceSetup;
    private measurementDeviceSetupInfo: MeasurementDeviceSetupInfo;

    private moduleInstance: IWorkflowModuleInstance;

    //#region UI Properties

    @computedFrom("measurementDeviceService.hardware.length", "measurementDeviceSetup")
    private get selectableMeasurementDevices(): MeasurementDevice[] {
        if (!this.measurementDeviceSetup) return [];

        const deviceInSetupIds = this.measurementDeviceSetup
            .filter((x) => !this.measurementDeviceSetupInfo || x.id !== this.measurementDeviceSetupInfo.id) // Make current device selectable aswell by "removing it from the setup"
            .map((x) => x.id);

        return this.measurementDeviceService.hardware.filter(
            (availableDevice) => !deviceInSetupIds.some((deviceInSetupId) => availableDevice.id === deviceInSetupId)
        );
    }

    @computedFrom("selectedMeasurementSetupId")
    private get hasValidMeasurementSetupId(): boolean {
        return (
            this.selectedMeasurementSetupId !== undefined &&
            this.selectedMeasurementSetupId !== null &&
            this.selectedMeasurementSetupId !== ""
        );
    }

    @computedFrom("selectedMeasurementDeviceSetupInfoId")
    private get hasValidMeasurementDeviceId(): boolean {
        return (
            this.selectedMeasurementDeviceSetupInfoId !== undefined &&
            this.selectedMeasurementDeviceSetupInfoId !== null &&
            this.selectedMeasurementDeviceSetupInfoId !== ""
        );
    }

    @computedFrom("measurementDeviceSetupInfo")
    private get deviceNotes(): string {
        return this.measurementDeviceSetupInfo ? this.measurementDeviceSetupInfo.notes : "";
    }

    private set deviceNotes(value: string) {
        this.measurementDeviceSetupInfo.notes = value;
        this.updateMeasurementSetup();
    }

    //#endregion

    //#region Controls
    private positionField: EditableField;
    //#endregion

    //#region Input Properties

    @computedFrom("measurementDeviceSetupInfo")
    private get xPosition(): number {
        if (!this.measurementDeviceSetupInfo) return undefined;

        return this.measurementDeviceSetupInfo.position.X;
    }
    private set xPosition(value: number) {
        if (!this.measurementDeviceSetupInfo) return;

        this.measurementDeviceSetupInfo.position.X = value;
        this.updateMeasurementSetup();
    }

    @computedFrom("measurementDeviceSetupInfo")
    private get yPosition(): number {
        if (!this.measurementDeviceSetupInfo) return undefined;

        return this.measurementDeviceSetupInfo.position.Y;
    }
    private set yPosition(value: number) {
        if (!this.measurementDeviceSetupInfo) return;

        this.measurementDeviceSetupInfo.position.Y = value;
        this.updateMeasurementSetup();
    }

    @computedFrom("measurementDeviceSetupInfo")
    private get zPosition(): number {
        if (!this.measurementDeviceSetupInfo) return undefined;

        return this.measurementDeviceSetupInfo.position.Z;
    }
    private set zPosition(value: number) {
        if (!this.measurementDeviceSetupInfo) return;

        this.measurementDeviceSetupInfo.position.Z = value;
        this.updateMeasurementSetup();
    }

    @observable
    private selectedFrontEndId: string;
    private selectedMeasurementDevice: MeasurementDevice;

    private newPosition: Point3f;
    private newFacingDirection: Point3f;
    private newUpDirection: Point3f;
    //#endregion

    //#region Constructor and Initialization
    constructor(
        private readonly measurementSetupRepository: MeasurementSetupRepository,
        private readonly measurementDeviceRepository: IGenericRepository<MeasurementDevice, MeasurementDevice>,
        private readonly measurementDeviceService: IHardwareService<MeasurementDevice>,
        private readonly frontEndService: IReservableHardwareService<FrontEnd, string>,
        private readonly measurementAcquisitionService: IMeasurementAcquisitionService,
        private readonly channelPresetService: IChannelSelectionPresetService
    ) {}

    public activateModule(moduleInstance: IWorkflowModuleInstance): void {
        this.selectedMeasurementSetupOrInfoIdChanged();
        this.moduleInstance = moduleInstance;
    }

    //#endregion

    //#region Property Changed Callbacks
    private selectedMeasurementSetupOrInfoIdChanged(): void {
        if (this.positionField) this.positionField.cancel();

        if (!this.hasValidMeasurementSetupId) {
            return;
        }

        this.measurementSetupRepository
            .getById(this.selectedMeasurementSetupId)
            .then((setup) => {
                return MeasurementDeviceSetup.fromRepository(setup, this.measurementDeviceRepository).then(
                    (measurementDeviceSetup) => {
                        this.measurementDeviceSetup = measurementDeviceSetup;

                        if (!this.hasValidMeasurementDeviceId) {
                            return;
                        } else {
                            const selectedDevice = measurementDeviceSetup.find(
                                (x) => x.id === this.selectedMeasurementDeviceSetupInfoId
                            );

                            if (selectedDevice) {
                                // Device info exists
                                this.measurementDeviceSetupInfo = selectedDevice;

                                this.measurementDeviceSetupInfo.channelSelection = Object.assign(
                                    new ChannelSelection(),
                                    this.measurementDeviceSetupInfo.channelSelection
                                );

                                if (selectedDevice.measurementDevice.frontEnd) {
                                    this.selectedFrontEndId = selectedDevice.measurementDevice.frontEnd.id;
                                } else {
                                    this.selectedFrontEndId = this.selectableMeasurementDevices[0]
                                        ? this.selectableMeasurementDevices[0].frontEnd.id
                                        : undefined;
                                }

                                const connectedDeviceSetup = ConnectedMeasurementDeviceSetup.fromDevice(
                                    this.measurementDeviceSetupInfo.measurementDevice,
                                    this.measurementDeviceSetupInfo.channelSelection
                                );
                                this.measurementAcquisitionService.setActiveSetup(connectedDeviceSetup);
                            } else {
                                throw new Error(
                                    `Measurement device with id "${this.selectedMeasurementDeviceSetupInfoId}
                                        " was not found in measurement setup "${setup.name}"`
                                );
                            }
                        }
                    }
                );
            })
            .catch((e: Error) => {
                this.moduleInstance
                    .createNotification()
                    .withUrgency(NotificationUrgency.Error)
                    .withMessage(e.message)
                    .show();
            });
    }

    public selectedFrontEndIdChanged(newValue: string, oldValue: string): void {
        if (this.selectedFrontEndId === this.selectedMeasurementDeviceSetupInfoId) return;

        const idx = this.measurementDeviceSetup.findIndex((x) => x.id === oldValue);
        if (idx === -1) {
            return;
        }

        this.selectedMeasurementDevice = this.measurementDeviceService.hardware.find(
            (x) => x.id === this.selectedFrontEndId
        );
        if (!this.selectedMeasurementDevice) {
            return;
        }

        this.measurementDeviceSetup[idx].id = this.selectedFrontEndId;
        this.measurementDeviceSetup[idx].measurementDevice = this.selectedMeasurementDevice;

        if (
            this.measurementDeviceSetup[idx].channelSelection.count !=
            this.selectedMeasurementDevice.frontEnd.microphoneCount
        ) {
            // Update channel selection
            this.measurementDeviceSetup[idx].channelSelection = this.channelPresetService.getDefaultPreset(
                this.selectedMeasurementDevice.frontEnd
            );
        }

        this.updateMeasurementSetup();
        this.selectedMeasurementDeviceSetupInfoId = this.selectedFrontEndId;
    }

    //#endregion

    //#region User Input
    private showChannelSelectionModal(): void {
        this.moduleInstance
            .createModal<ChannelSelectionModalState, ChannelSelectionModalReturnState>({
                ...WorkflowModalCatalog.ChannelSelectionModal,
                smallModalSize: this.measurementAcquisitionService.activeSetup.sensorPositions.length <= 64,
            })
            .withState({ frontEnd: this.measurementDeviceSetupInfo.measurementDevice.frontEnd })
            .whenClosed((x) => {
                // Update selection
                this.measurementDeviceSetupInfo.channelSelection = x.selectedSelection;
                return this.updateMeasurementSetup();
            })
            .show();
    }

    //#endregion

    //#region Helper Methods
    private updateMeasurementSetup(): Promise<void> {
        const currentCaptureDevice = this.measurementDeviceSetup.captureDevices[0];

        const measurementDeviceSetupStub = MeasurementDeviceSetup.toStub(this.measurementDeviceSetup);

        // Store in db
        return this.measurementSetupRepository
            .put(measurementDeviceSetupStub)
            .then((updatedSetupStub) => {
                // Reload if this is the active setup
                if (
                    this.measurementAcquisitionService.activeSetup &&
                    this.measurementAcquisitionService.activeSetup.id === updatedSetupStub.id
                ) {
                    // Check if this setup is connected, if so, select it
                    return ConnectedMeasurementDeviceSetup.fromConnectedDevices(
                        updatedSetupStub,
                        this.measurementDeviceService
                    )
                        .then((updatedSetup) => {
                            // Remove all capture devices except the one that is selected
                            updatedSetup.forEach((x) => {
                                const captureDevice = currentCaptureDevice
                                    ? x.measurementDevice.captureDevices.find((y) => y.id === currentCaptureDevice.id)
                                    : undefined;
                                x.measurementDevice.captureDevices = captureDevice ? [captureDevice] : [];
                            });

                            this.measurementAcquisitionService.setActiveSetup(updatedSetup);
                        })
                        .catch((e) => {
                            console.error(e);
                            this.measurementAcquisitionService.setActiveSetup(null);
                        });
                }
            })
            .catch((e: Error) => {
                this.moduleInstance
                    .createNotification()
                    .withUrgency(NotificationUrgency.Error)
                    .withMessage(e.message)
                    .show();
            });
    }
    //#endregion
}
